import React, { useContext, useState, useEffect, createContext } from 'react'
import type { Container } from 'inversify'
import { isComponentLifetime } from '@core/interfaces/component-lifetime'

const InversifyContext = createContext<Container | null>(null)

export function Provider({ container, children }: { container: Container; children: React.ReactNode }) {
  return <InversifyContext.Provider value={container}>{children}</InversifyContext.Provider>
}

export function useInjection<T>(identifier: symbol | string | (new (...args: any[]) => T)) {
  const container = useContext(InversifyContext)
  if (!container) {
    throw new Error('Container not found. Please wrap your app with <Provider container={container} />')
  }
  // Resolve once per component instance, not per render — presenters are transient-scoped
  // by default, so re-resolving on every render silently swapped in a fresh instance and
  // dropped any local state (e.g. NavigationPresenter's expandedNodes).
  const [instance] = useState(() => container.get<T>(identifier))

  // Disposal counterpart to the resolve-once-per-mount above: only classes
  // explicitly implementing ComponentLifetime are disposed (a Singleton
  // Presenter, or one that merely happens to expose a `dispose()` method, is
  // left alone). Runs once per mount/unmount, not per render, because
  // `instance` is stable for the component's lifetime (see useState above) —
  // see component-lifetime.ts for why this exists.
  useEffect(() => {
    return () => {
      if (isComponentLifetime(instance)) instance.disposeComponent()
    }
  }, [instance])

  return instance
}
